import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import Modal from 'react-modal';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import { ErrorContext } from '../../context/ErrorContext';
import { AuthContext } from '../../context/AuthContext';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import './css/AddCityModal.css';

const apiUrl = API_URL;

const DEFAULT_PAGE_SIZE = 10;

const normalizeId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return String(value._id || value.id || '');
  }
  return String(value);
};

const AddCityModal = ({
  isOpen,
  onRequestClose,
  onSelectCommander,
  selectedNationId,
  selectedPlanId,
  commanderNationOptions = [],
  cityPlacementByCommanderId = {}
}) => {
  const axios = axiosInstance;
  const { showError } = useContext(ErrorContext);
  const { user } = useContext(AuthContext);

  const [commanderQuery, setCommanderQuery] = useState('');
  const [commanderNationId, setCommanderNationId] = useState('');
  const [allianceFilterId, setAllianceFilterId] = useState('');
  const [commanders, setCommanders] = useState([]);
  const [alliances, setAlliances] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingCommanders, setIsLoadingCommanders] = useState(false);
  const [isLoadingAlliances, setIsLoadingAlliances] = useState(false);

  const [createName, setCreateName] = useState('');
  const [createBattlePower, setCreateBattlePower] = useState('');
  const [createAllianceId, setCreateAllianceId] = useState('');
  const [isCreatingCommander, setIsCreatingCommander] = useState(false);
  const [mobilePane, setMobilePane] = useState('select');

  const [modalError, setModalError] = useState('');
  const [pendingRelocationCommander, setPendingRelocationCommander] = useState(null);

  const selectedNationIdNormalized = normalizeId(selectedNationId);
  const selectedPlanIdNormalized = normalizeId(selectedPlanId);
  const currentUserNationId = normalizeId(user?.nation);
  const normalizedCommanderNationOptions = useMemo(() => {
    const optionById = new Map();
    commanderNationOptions.forEach((nation) => {
      const nationId = normalizeId(nation?._id || nation?.id || nation);
      if (!nationId || optionById.has(nationId)) return;
      optionById.set(nationId, nation);
    });

    if (selectedNationIdNormalized && !optionById.has(selectedNationIdNormalized)) {
      optionById.set(selectedNationIdNormalized, {
        _id: selectedNationIdNormalized,
        number: selectedNationIdNormalized
      });
    }

    return Array.from(optionById.values()).sort((left, right) => {
      const leftNumber = Number(left?.number ?? 0);
      const rightNumber = Number(right?.number ?? 0);
      return leftNumber - rightNumber;
    });
  }, [commanderNationOptions, selectedNationIdNormalized]);
  const selectedCommanderNationIdNormalized = useMemo(() => {
    const explicitSelection = normalizeId(commanderNationId);
    if (explicitSelection) return explicitSelection;
    return selectedNationIdNormalized;
  }, [commanderNationId, selectedNationIdNormalized]);
  const selectedCommanderNation = useMemo(
    () =>
      normalizedCommanderNationOptions.find(
        (nation) => normalizeId(nation?._id || nation?.id || nation) === selectedCommanderNationIdNormalized
      ) || null,
    [normalizedCommanderNationOptions, selectedCommanderNationIdNormalized]
  );
  const canCreateCommanderInSelectedNation =
    user?.role !== 'nationAdmin' || selectedCommanderNationIdNormalized === currentUserNationId;
  const getDefaultCommanderNationId = useCallback(() => {
    if (!normalizedCommanderNationOptions.length) {
      return selectedNationIdNormalized;
    }
    const hasSelectedMapNation = normalizedCommanderNationOptions.some(
      (nation) => normalizeId(nation?._id || nation?.id || nation) === selectedNationIdNormalized
    );
    if (hasSelectedMapNation) {
      return selectedNationIdNormalized;
    }
    return normalizeId(normalizedCommanderNationOptions[0]?._id || normalizedCommanderNationOptions[0]);
  }, [normalizedCommanderNationOptions, selectedNationIdNormalized]);

  const selectedAllianceFilter = useMemo(() => {
    if (!allianceFilterId) return null;
    return alliances.find((alliance) => normalizeId(alliance._id) === allianceFilterId) || null;
  }, [allianceFilterId, alliances]);

  const parseBattlePower = (value) => {
    const cleaned = String(value || '').replace(/[^0-9]/g, '');
    if (!cleaned) return null;
    const parsed = Number.parseInt(cleaned, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatBattlePower = (value) => {
    const parsed = parseBattlePower(value);
    if (!parsed) return '';
    return parsed.toLocaleString();
  };

  const fetchAlliances = useCallback(async () => {
    if (!selectedCommanderNationIdNormalized) {
      setAlliances([]);
      return;
    }

    setIsLoadingAlliances(true);
    try {
      const response = await axios.get(`${apiUrl}/nations/${selectedCommanderNationIdNormalized}/alliances`);
      const sortedAlliances = (response.data || []).sort((a, b) => {
        const aLabel = `${a.acronym || ''} ${a.name || ''}`.trim().toLowerCase();
        const bLabel = `${b.acronym || ''} ${b.name || ''}`.trim().toLowerCase();
        return aLabel.localeCompare(bLabel);
      });
      setAlliances(sortedAlliances);
    } catch (error) {
      setAlliances([]);
      setModalError('Failed to load alliances.');
      showError(error?.response?.data?.message || 'Failed to load alliances.');
    } finally {
      setIsLoadingAlliances(false);
    }
  }, [axios, selectedCommanderNationIdNormalized, showError]);

  const fetchCommanders = useCallback(async (page = 1) => {
    if (!selectedCommanderNationIdNormalized) {
      setCommanders([]);
      setTotalPages(1);
      return;
    }

    const prioritizeUnplacedInSelectedMap =
      selectedCommanderNationIdNormalized === selectedNationIdNormalized;

    setIsLoadingCommanders(true);
    try {
      const response = await axios.get(`${apiUrl}/nations/${selectedCommanderNationIdNormalized}/commanders/search`, {
        params: {
          name: commanderQuery,
          page,
          limit: DEFAULT_PAGE_SIZE,
          allianceId: allianceFilterId || undefined,
          prioritizeUnplaced: prioritizeUnplacedInSelectedMap,
          planId: prioritizeUnplacedInSelectedMap ? selectedPlanIdNormalized || undefined : undefined
        }
      });

      setCommanders(response.data?.commanders || []);
      setTotalPages(Math.max(1, response.data?.totalPages || 1));
    } catch (error) {
      setCommanders([]);
      setTotalPages(1);
      setModalError('Failed to load commanders.');
      showError(error?.response?.data?.message || 'Failed to load commanders.');
    } finally {
      setIsLoadingCommanders(false);
    }
  }, [allianceFilterId, axios, commanderQuery, selectedCommanderNationIdNormalized, selectedNationIdNormalized, selectedPlanIdNormalized, showError]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setCommanderQuery('');
    setCurrentPage(1);
    setTotalPages(1);
    setModalError('');
    setPendingRelocationCommander(null);
    setCreateName('');
    setCreateBattlePower('');
    setCreateAllianceId('');
    setMobilePane('select');
    setCommanderNationId(getDefaultCommanderNationId());
  }, [getDefaultCommanderNationId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    fetchAlliances();
  }, [fetchAlliances, isOpen]);

  useEffect(() => {
    // Keep alliance filter sticky across opens, but reset on commander nation change.
    setAllianceFilterId('');
    setCreateAllianceId('');
  }, [selectedCommanderNationIdNormalized]);

  useEffect(() => {
    if (!isOpen) return;
    fetchCommanders(currentPage);
  }, [currentPage, fetchCommanders, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentPage(1);
    setPendingRelocationCommander(null);
  }, [commanderQuery, allianceFilterId, isOpen]);

  const getCommanderPlacement = (commanderId) => {
    const key = normalizeId(commanderId);
    return cityPlacementByCommanderId[key] || null;
  };

  const finalizeCommanderSelection = (commander, options = {}) => {
    setPendingRelocationCommander(null);
    onSelectCommander(commander, options);
    onRequestClose();
  };

  const handleCommanderClick = (commander) => {
    const placement = getCommanderPlacement(commander._id);

    if (placement) {
      setPendingRelocationCommander({ commander, placement });
      return;
    }

    finalizeCommanderSelection(commander, { allowRelocateExisting: false });
  };

  const handleRelocateConfirm = () => {
    if (!pendingRelocationCommander?.commander) return;

    finalizeCommanderSelection(pendingRelocationCommander.commander, {
      allowRelocateExisting: true
    });
  };

  const handleCreateCommander = async () => {
    const name = createName.trim();
    const battlePower = parseBattlePower(createBattlePower);

    if (!selectedCommanderNationIdNormalized) {
      setModalError('Select a nation before creating commanders.');
      return;
    }
    if (!canCreateCommanderInSelectedNation) {
      setModalError('Your role cannot create commanders in this nation.');
      return;
    }
    if (!name) {
      setModalError('Commander name is required.');
      return;
    }
    if (!battlePower) {
      setModalError('Battle Power is required.');
      return;
    }

    setIsCreatingCommander(true);
    setModalError('');

    try {
      const payload = {
        name,
        battlePower,
        alliance: createAllianceId || null
      };
      const response = await axios.post(`${apiUrl}/nations/${selectedCommanderNationIdNormalized}/commanders`, payload);
      const createdCommander = response.data;
      finalizeCommanderSelection(createdCommander, { allowRelocateExisting: false });
    } catch (error) {
      setModalError(error?.response?.data?.message || 'Failed to create commander.');
    } finally {
      setIsCreatingCommander(false);
    }
  };

  const commandersWithPlacement = useMemo(() => (
    commanders
      .map((commander) => ({
        commander,
        placement: cityPlacementByCommanderId[normalizeId(commander._id)] || null
      }))
      .sort((left, right) => {
        const leftPlaced = !!left.placement;
        const rightPlaced = !!right.placement;
        if (leftPlaced !== rightPlaced) {
          return leftPlaced ? 1 : -1;
        }

        const leftBp = Number(left.commander?.battlePower || 0);
        const rightBp = Number(right.commander?.battlePower || 0);
        if (leftBp !== rightBp) {
          return rightBp - leftBp;
        }

        return String(left.commander?.name || '').localeCompare(String(right.commander?.name || ''));
      })
  ), [commanders, cityPlacementByCommanderId]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Add City"
      className="add-city-modal"
      overlayClassName="add-city-modal-overlay"
    >
      <div className="add-city-modal-header">
        <h2>Add City</h2>
        <p>Select existing commander or create a new commander, then place city on selected tile.</p>
      </div>

      {modalError && <div className="add-city-modal-error">{modalError}</div>}

      <div className="add-city-mobile-toggle" role="tablist" aria-label="Add city mobile mode">
        <button
          type="button"
          className={`add-city-mobile-toggle-btn ${mobilePane === 'select' ? 'active' : ''}`}
          onClick={() => setMobilePane('select')}
        >
          Select Existing
        </button>
        <button
          type="button"
          className={`add-city-mobile-toggle-btn ${mobilePane === 'create' ? 'active' : ''}`}
          onClick={() => setMobilePane('create')}
        >
          Create New
        </button>
      </div>

      <div className={`add-city-modal-grid mobile-pane-${mobilePane}`}>
        <section className="add-city-modal-column add-city-select-column">
          <h3>Select Existing</h3>
          <div className="add-city-filter-row">
            <div className="add-city-filter-field">
              <label htmlFor="city-commander-nation">Commander Nation</label>
              <select
                id="city-commander-nation"
                value={selectedCommanderNationIdNormalized}
                onChange={(event) => setCommanderNationId(event.target.value)}
              >
                {normalizedCommanderNationOptions.map((nationOption) => {
                  const optionId = normalizeId(nationOption?._id || nationOption?.id || nationOption);
                  const optionLabel = nationOption?.number ?? optionId;
                  return (
                    <option key={optionId} value={optionId}>
                      {optionLabel}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="add-city-filter-field add-city-filter-alliance">
              <label htmlFor="city-alliance-filter-dropdown">Alliance</label>
              <Autocomplete
                id="city-alliance-filter-dropdown"
                options={alliances}
                value={selectedAllianceFilter}
                loading={isLoadingAlliances}
                disableClearable={false}
                isOptionEqualToValue={(option, value) => normalizeId(option?._id) === normalizeId(value?._id)}
                getOptionLabel={(option) => `[${option?.acronym || '---'}] ${option?.name || ''}`.trim()}
                onChange={(_, value) => {
                  setAllianceFilterId(value ? normalizeId(value._id) : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="All alliances"
                    InputLabelProps={{ shrink: false }}
                  />
                )}
              />
            </div>

            <div className="add-city-filter-field">
              <label htmlFor="city-commander-search">Commander Search</label>
              <input
                id="city-commander-search"
                type="text"
                value={commanderQuery}
                placeholder="Search commander name"
                onChange={(event) => setCommanderQuery(event.target.value)}
              />
            </div>
          </div>

          <div className="add-city-commander-list-wrapper">
            <div className="add-city-commander-list" role="listbox" aria-label="Commander options">
              {commandersWithPlacement.map(({ commander, placement }) => (
                <button
                  key={commander._id}
                  type="button"
                  className={`add-city-commander-item ${placement ? 'placed' : ''}`}
                  onClick={() => handleCommanderClick(commander)}
                >
                  <span className="add-city-commander-left">
                    <span className="add-city-commander-alliance">[{commander.alliance?.acronym || '---'}]</span>
                    <span className="add-city-commander-name">{commander.name}</span>
                  </span>
                  <span className={`add-city-commander-coords ${placement ? 'has-placement' : ''}`}>
                    {placement ? `(${placement.x}, ${placement.y})` : ''}
                  </span>
                  <span className="add-city-commander-bp">{Number(commander.battlePower || 0).toLocaleString()}</span>
                </button>
              ))}
              {!isLoadingCommanders && commandersWithPlacement.length === 0 && (
                <p className="add-city-list-meta">No commanders found for current filters.</p>
              )}
            </div>

            {isLoadingCommanders && (
              <div className="add-city-list-loading-overlay">
                <p className="add-city-list-meta">Loading commanders...</p>
              </div>
            )}

            {pendingRelocationCommander && (
              <div className="add-city-relocate-overlay">
                <div className="add-city-relocate-confirm">
                  <p>
                    <strong>{pendingRelocationCommander.commander.name}</strong> already has a city at
                    {' '}
                    ({pendingRelocationCommander.placement.x}, {pendingRelocationCommander.placement.y}).
                  </p>
                  <p>Move this city to the selected destination?</p>
                  <div className="add-city-relocate-actions">
                    <button type="button" className="danger" onClick={handleRelocateConfirm}>
                      Confirm Move
                    </button>
                    <button type="button" onClick={() => setPendingRelocationCommander(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="add-city-pagination">
            <button type="button" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage <= 1}>
              Previous
            </button>
            <span>{currentPage} / {totalPages}</span>
            <button type="button" onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage >= totalPages}>
              Next
            </button>
          </div>
        </section>

        <section className="add-city-modal-column add-city-create-column">
          <h3>Create New</h3>
          <p className="add-city-list-meta">
            Nation: {(selectedCommanderNation?.number ?? selectedCommanderNationIdNormalized) || 'N/A'}
          </p>
          {!canCreateCommanderInSelectedNation && (
            <p className="add-city-list-meta">
              Nation Admins can only create commanders in their own nation.
            </p>
          )}
          <label htmlFor="create-commander-name">Commander Name *</label>
          <input
            id="create-commander-name"
            type="text"
            value={createName}
            placeholder="Enter commander name"
            onChange={(event) => setCreateName(event.target.value)}
            disabled={!canCreateCommanderInSelectedNation}
          />

          <label htmlFor="create-commander-bp">Battle Power *</label>
          <input
            id="create-commander-bp"
            type="text"
            value={formatBattlePower(createBattlePower)}
            placeholder="Enter battle power"
            onChange={(event) => setCreateBattlePower(event.target.value)}
            disabled={!canCreateCommanderInSelectedNation}
          />

          <label htmlFor="create-commander-alliance">Alliance</label>
          <select
            id="create-commander-alliance"
            value={createAllianceId}
            onChange={(event) => setCreateAllianceId(event.target.value)}
            disabled={isLoadingAlliances || !canCreateCommanderInSelectedNation}
          >
            <option value="">None</option>
            {alliances.map((alliance) => (
              <option key={alliance._id} value={alliance._id}>
                [{alliance.acronym}] {alliance.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="add-city-create-button"
            onClick={handleCreateCommander}
            disabled={isCreatingCommander || !canCreateCommanderInSelectedNation}
          >
            {isCreatingCommander ? 'Creating...' : 'Create And Place'}
          </button>
        </section>
      </div>

      <div className="add-city-modal-footer">
        <button type="button" onClick={onRequestClose}>Cancel</button>
      </div>
    </Modal>
  );
};

export default AddCityModal;
